import { create } from "zustand";

import type { StipulationApplicantScope, StipulationCategory } from "./stipulationCategoryFilters";
import { ALL_STIPULATION_CATEGORIES, DEFAULT_STIPULATION_CATEGORY } from "./stipulationCategoryFilters";

type CategoryFilter = StipulationCategory | typeof ALL_STIPULATION_CATEGORIES;

// A manual dropdown pick has no applicant context, and side-by-side sections
// (both signer columns aligned on the same category) should show both — so
// "both" is the safe default whenever the scroll-spy isn't narrowing to a
// single column.
const DEFAULT_APPLICANT_SCOPE: StipulationApplicantScope = "both";

/**
 * The 3 fields that make up the "resolved" filter (as opposed to the pending
 * one below) always change together — this factory is the one place that
 * pairing is spelled out, so every mutator below builds its `set(...)` patch
 * through it. A future 4th correlated field only needs adding here; every
 * call site then fails to compile until it's updated too, instead of quietly
 * omitting it.
 * @param {CategoryFilter} category - The active category filter.
 * @param {StipulationApplicantScope} applicantScope - Which applicant(s) the category is scoped to.
 * @param {boolean} scrollScoped - Whether the filter reflects a real scroll-spy report.
 * @returns {object} The patch for the resolved filter fields.
 */
function resolvedFilter(category: CategoryFilter, applicantScope: StipulationApplicantScope, scrollScoped: boolean) {
  return { category, applicantScope, scrollScoped };
}

/**
 * Same pairing guarantee as `resolvedFilter`, for the pending (not-yet-armed) scroll report.
 * @param {CategoryFilter} category - The pending category.
 * @param {StipulationApplicantScope} applicantScope - The pending applicant scope.
 * @returns {object} The patch for the pending filter fields.
 */
function pendingFilter(category: CategoryFilter, applicantScope: StipulationApplicantScope) {
  return { pendingScrollCategory: category, pendingApplicantScope: applicantScope };
}

interface StipulationCategoryState {
  /** The active category filter — a `StipulationCategory` code or the "all" sentinel. */
  category: CategoryFilter;
  /**
   * Which applicant(s) the active category's visible stips are scoped to.
   * Only meaningful for the scroll-tied categories (Income/Bank/Residence);
   * always "both" for a manual dropdown pick or any other category.
   */
  applicantScope: StipulationApplicantScope;
  /**
   * Whether `category`/`applicantScope` currently reflect a real scroll-spy
   * report (vs. a manual dropdown pick, which has no applicant context).
   * The panel only applies applicant filtering/Driver-first ordering when
   * this is `true` — a manual pick must leave the server's own order intact.
   */
  scrollScoped: boolean;
  /**
   * Whether the scroll-spy is allowed to narrow `category` yet. Stays `false`
   * from page load until the analyst actually scrolls (past a small
   * threshold) or types into a review section — see `arm()`. Before that,
   * scroll-spy updates are recorded in `pendingScrollCategory` but withheld
   * from `category`, so the panel shows "All Stips" on first render instead
   * of flashing to whatever section happens to occupy the activation band
   * on mount (IntersectionObserver always fires once immediately on
   * `observe()`, independent of any real user scroll).
   */
  armed: boolean;
  /**
   * The most recent section reported by the scroll-spy, applied retroactively
   * once `arm()` fires. Can be the "all" sentinel — the scroll-spy reports
   * that when the analyst has scrolled back above the first section (nothing
   * intersecting the activation band), so the filter reverts to "All Stips".
   */
  pendingScrollCategory: CategoryFilter | undefined;
  /** The applicant scope paired with `pendingScrollCategory`, applied together once armed. */
  pendingApplicantScope: StipulationApplicantScope;
  /**
   * Whether the review sections' scroll container is currently scrolled all
   * the way to the top. Tracked independently of the scroll-spy's
   * IntersectionObserver because that observer can't be trusted at the exact
   * top of the page — Income (the first section) can still overlap the
   * activation band at `scrollTop === 0`, depending on layout, so it never
   * reports "nothing intersecting" there. While `atTop` is `true`,
   * `setCategoryFromScroll` ignores any non-"all" report from the scroll-spy.
   */
  atTop: boolean;
  /** Sets the active category filter directly. Used by the manual dropdown — always applies immediately, armed or not, scopes to "both", and clears `scrollScoped` (a dropdown pick has no applicant context). */
  setCategory: (category: CategoryFilter) => void;
  /**
   * Called by the review page's scroll-spy as the analyst scrolls through its
   * sections, or back above all of them (`ALL_STIPULATION_CATEGORIES`).
   * `applicantScope` is "both" when both signer columns' matching section are
   * simultaneously in view (side by side) or the section has no applicant
   * column (e.g. Vehicle), otherwise the one column that is.
   * Only updates `category`/`applicantScope` once armed. Ignored while
   * `atTop` is `true` and the reported category isn't "all" — see `atTop`.
   */
  setCategoryFromScroll: (category: CategoryFilter, applicantScope: StipulationApplicantScope) => void;
  /**
   * Called by `useArmStipulationCategoryFilter` on every relevant scroll to
   * report whether the review sections' scroll container is at `scrollTop 0`.
   * Snaps straight to "All Stips" as soon as it becomes `true`, taking
   * priority over the scroll-spy (see `atTop`).
   */
  setScrollAtTop: (atTop: boolean) => void;
  /** Allows scroll-spy updates to reach `category`/`applicantScope`; snaps to the last-known scroll section immediately. Idempotent. */
  arm: () => void;
  /** Resets to the default ("All Stips"), unarmed state (e.g. on deal switch). */
  reset: () => void;
}

/**
 * Shared category filter for the Stipulations panel. Lives outside the panel
 * component so the review page's scroll-spy (a sibling in the layout) can
 * drive it directly as the analyst scrolls through the review sections.
 */
export const useStipulationCategoryStore = create<StipulationCategoryState>((set, get) => ({
  category: DEFAULT_STIPULATION_CATEGORY,
  applicantScope: DEFAULT_APPLICANT_SCOPE,
  scrollScoped: false,
  armed: false,
  pendingScrollCategory: undefined,
  pendingApplicantScope: DEFAULT_APPLICANT_SCOPE,
  atTop: false,
  setCategory: (category) => set(resolvedFilter(category, DEFAULT_APPLICANT_SCOPE, false)),
  setCategoryFromScroll: (category, applicantScope) => {
    // At the exact top of the page, the scroll-spy's IntersectionObserver
    // can still be reporting a stale section (Income can overlap the
    // activation band at scrollTop 0) — `setScrollAtTop` already forced
    // "All Stips" in that case, so ignore anything but a matching "all"
    // report until the analyst actually scrolls away from the top.
    if (get().atTop && category !== ALL_STIPULATION_CATEGORIES) {
      return;
    }
    set(pendingFilter(category, applicantScope));
    if (get().armed) {
      set(resolvedFilter(category, applicantScope, true));
    }
  },
  setScrollAtTop: (atTop) => {
    set({ atTop });
    if (atTop) {
      set({
        ...resolvedFilter(ALL_STIPULATION_CATEGORIES, DEFAULT_APPLICANT_SCOPE, false),
        ...pendingFilter(ALL_STIPULATION_CATEGORIES, DEFAULT_APPLICANT_SCOPE),
      });
    }
  },
  arm: () => {
    if (get().armed) {
      return;
    }
    const { pendingScrollCategory, pendingApplicantScope } = get();
    set({
      armed: true,
      ...(pendingScrollCategory ? resolvedFilter(pendingScrollCategory, pendingApplicantScope, true) : {}),
    });
  },
  reset: () =>
    set({
      ...resolvedFilter(DEFAULT_STIPULATION_CATEGORY, DEFAULT_APPLICANT_SCOPE, false),
      armed: false,
      pendingScrollCategory: undefined,
      pendingApplicantScope: DEFAULT_APPLICANT_SCOPE,
      atTop: false,
    }),
}));
